import React from 'react';
import { MAX_BREAKDOWN_COHORTS } from '../../../../config/constants';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { addCohortRow, RowType } from '../../../../redux/slices/oldBuilder';
import { TextButton } from '../../../Button';
import DndDroppableZone from '../../../DndEntity/DndDroppableZone';
import DropzoneArea from '../../../DndEntity/DndDroppableZone/DropzoneArea';
import { useDndEntityInternalContext } from '../../../DndEntity';
import {
  EntityDropzoneDataTypeEnum,
  ICohortEntityDropzoneDataType,
  IEventEntityDropzoneDataType,
  IMeasureEntityDropzoneDataType,
} from '../../../DndEntity/validation';
import { EntityDropzoneType } from '../../../QuickAccessDictionary/DictionaryTabs/types';
import { getDndActiveItemData } from '../../../DndEntity/utils';
import useQueryBuilderDroppable from '../../../DndEntity/useQueryBuilderDroppable';
import KubitTooltip from '../../../KubitTooltip';

interface IProps {
  allowAddSegment: boolean;
}

const cohortDropzoneId = 'Entity-Global-Segment-Dropzone';
const eventDropzoneId = 'Entity-Event-Segment-Dropzone';
const measureDropzoneId = 'Entity-Measure-Segment-Dropzone';

const AddSegmentAction = ({ allowAddSegment }: IProps) => {
  const dispatch = useAppDispatch();
  const segments = useAppSelector(
    ({ builder }) => builder[RowType.breakdownCohorts],
  );
  const { activeItem } = useDndEntityInternalContext();
  const { isBasicBuilderComplete } = useQueryBuilderDroppable();

  const getShowAddDropzone = (types: string[]) => {
    if (segments.length >= MAX_BREAKDOWN_COHORTS) {
      return false;
    }

    const data = getDndActiveItemData(activeItem);

    return (
      data &&
      allowAddSegment &&
      types.includes(data.__typename) &&
      isBasicBuilderComplete
    );
  };

  const showAddCohortFilterDropzone = getShowAddDropzone([
    'TagLeafSimpleCohort',
    'TagLeafCompoundCohort',
  ]);
  const showAddEventDropzone = getShowAddDropzone([
    'TagLeafAnyEvent',
    'TagLeafBasicEvent',
    'TagLeafVirtualEvent',
  ]);
  const showAddMeasureDropzone = getShowAddDropzone([
    'TagLeafSimpleMeasure',
    'TagLeafCompoundMeasure',
  ]);

  const handleAddSegment = () => {
    if (!allowAddSegment) {
      return;
    }

    dispatch(
      addCohortRow({
        rowType: RowType.breakdownCohorts,
        limit: MAX_BREAKDOWN_COHORTS,
      }),
    );
  };

  if (showAddCohortFilterDropzone) {
    return (
      <DndDroppableZone
        id={cohortDropzoneId}
        accepts={[EntityDropzoneType.Cohorts]}
        data={
          {
            id: cohortDropzoneId,
            type: EntityDropzoneDataTypeEnum.Cohorts,
            rowMeta: {
              rowType: RowType.breakdownCohorts,
              rowIndex: segments.length,
            },
          } satisfies ICohortEntityDropzoneDataType
        }
      >
        <DropzoneArea size="large">+ Add Cohort as New Segment</DropzoneArea>
      </DndDroppableZone>
    );
  }

  if (showAddEventDropzone) {
    return (
      <DndDroppableZone
        id={eventDropzoneId}
        accepts={[EntityDropzoneType.Events]}
        data={
          {
            id: eventDropzoneId,
            type: EntityDropzoneDataTypeEnum.Events,
            rowMeta: {
              rowType: RowType.breakdownCohorts,
              rowIndex: segments.length,
              eventIndex: 0,
            },
            addCohortRow: true,
          } satisfies IEventEntityDropzoneDataType
        }
      >
        <DropzoneArea size="large">+ Add Event as New Segment</DropzoneArea>
      </DndDroppableZone>
    );
  }

  if (showAddMeasureDropzone) {
    return (
      <DndDroppableZone
        id={measureDropzoneId}
        accepts={[EntityDropzoneType.Measures]}
        data={
          {
            id: measureDropzoneId,
            type: EntityDropzoneDataTypeEnum.Measures,
            rowMeta: {
              rowType: RowType.breakdownCohorts,
              rowIndex: segments.length,
            },
            addCohortRow: true,
          } satisfies IMeasureEntityDropzoneDataType
        }
      >
        <DropzoneArea size="large">+ Add Measure as New Segment</DropzoneArea>
      </DndDroppableZone>
    );
  }

  return (
    <KubitTooltip
      content={'Add cohort segment(s) for comparison purposes.'}
      placement="bottom"
      disabled={!allowAddSegment}
    >
      <TextButton onClick={handleAddSegment} disabled={!allowAddSegment}>
        + Add Segment
      </TextButton>
    </KubitTooltip>
  );
};

export default AddSegmentAction;
